import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, interval } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { MfaChallengeService } from '../../services/mfa-challenge.service';
import { MfaReAuthService } from '../../services/mfa-reauth.service';
import { ErrorService } from '../../services/error.service';

export type ChallengeStep = 'loading' | 'email-otp' | 'totp' | 'contact-admin' | 'success';

/**
 * LOCALIZATION: user-visible copy, kept here as named constants rather than inline in the template so the
 * MFA localization task has ONE place to convert instead of two duplicated template literals.
 *
 * Not yet wired to the `translate` pipe — the whole MFA surface is still hardcoded English and its localization
 * is a tracked follow-up this PR does NOT close. This is a coverage gap, not a safety constraint:
 * `CustomLoader.getTranslation` serves DEFAULT from the checked-in `src/customizations/languages/standard.json`
 * and `app.component.ts` runs `setDefaultLang('DEFAULT')` + `use('CUSTOM')` with ngx-translate's `useDefaultLang`,
 * so a key missing from the DB-driven `tblWebTranslations` catalogue resolves against the in-repo JSON. The
 * earlier "Nullable object" failure was a DIFFERENT mechanism; ids can be shipped safely by adding their rows to
 * standard.json first. Converting these along with the rest of the MFA copy is that follow-up's job.
 */
const SWITCH_FALLBACK_LABEL = 'Try another method';
const SWITCH_METHOD_LABELS: Record<string, string> = {
	EmailOtp: 'Use an email code instead',
	AuthenticatorApp: 'Use my authenticator app instead'
};

export interface MfaChallengeDialogData {
	challengeSessionId: string;
	preferredMethod: string;
	availableMethods: string[];
	maskedEmail: string;
	otpTtlMinutes: number;
	otpResendDelaySeconds: number;
	maxAttempts: number;
	custId?: number;
	contactAdminPhone?: string;
	contactAdminEmail?: string;
	mode?: 'login' | 'customerSwitch' | 'reauth';
}

export interface MfaChallengeResult {
	success: boolean;
	mfaSessionToken?: string;
	reAuthToken?: string;
	signedOut?: boolean;
	// The method that was verified. Passing an Email OTP re-proves the address, so the backend flips
	// emailVerified back on; the caller uses this to refresh the cached flag without a re-login.
	method?: string;
}

@Component({
	selector: 'app-mfa-challenge',
	templateUrl: './mfa-challenge.component.html',
	styleUrls: ['./mfa-challenge.component.scss']
})
export class MfaChallengeComponent implements OnInit, OnDestroy {
	public currentStep: ChallengeStep = 'loading';
	public code = '';
	public errorMessage = '';
	public isSubmitting = false;
	public attemptsRemaining: number | null = null;
	public otpExpiresAt: Date | null = null;
	public otpCountdown = 0;
	public resendCountdown = 0;
	public currentMethod = '';
	public originalMethod = '';
	public hasSwitched = false;
	public isOnAlternativeMethod = false;
	public contactAdminPhone = '';
	public contactAdminEmail = '';
	public contactAdminPhoneEnabled = false;
	public contactAdminEmailEnabled = false;
	public canSwitchMethod = false;
	/**
	 * Label for the switch control, naming the factor the user would move TO.
	 *
	 * availableMethods was already fetched and passed in, but only ever length-checked — so the control said
	 * "Try another method" without saying which, and the switch is one-shot with the link vanishing afterwards.
	 * That opacity is the likeliest reason the original report was unsure what it had seen (CHK-04 finding 6).
	 */
	public switchMethodLabel = SWITCH_FALLBACK_LABEL;
	public isOtpExpired = false;
	public canResend = false;
	public formattedCountdown = '0:00';

	private destroy$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<MfaChallengeComponent>,
		@Inject(MAT_DIALOG_DATA) public data: MfaChallengeDialogData,
		private _challengeService: MfaChallengeService,
		private _reAuthService: MfaReAuthService,
		private _errorService: ErrorService
	) {}

	ngOnInit(): void {
		this.currentMethod = this.data.preferredMethod;
		this.originalMethod = this.data.preferredMethod;
		this.contactAdminPhone = this.data.contactAdminPhone || '';
		this.contactAdminEmail = this.data.contactAdminEmail || '';
		this.startChallenge();
	}

	ngOnDestroy(): void {
		this.code = '';
		this.destroy$.next();
		this.destroy$.complete();
	}

	startChallenge(): void {
		this.currentStep = 'loading';
		this.isSubmitting = true;
		this.errorMessage = '';

		const start$ = this.data.mode === 'reauth'
			? this._reAuthService.startReAuth(this.currentMethod, this.data.challengeSessionId)
			: this._challengeService.startChallenge(this.currentMethod, this.data.challengeSessionId);

		start$
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.isSubmitting = false;
					if (result.otpExpiresAt) {
						this.otpExpiresAt = new Date(result.otpExpiresAt);
						this.startOtpCountdown();
					}
					this.startResendCountdown(this.data.otpResendDelaySeconds);
					this.currentStep = this.currentMethod === 'EmailOtp' ? 'email-otp' : 'totp';
					this.updateComputedProperties();
				},
				error: (err) => {
					this.isSubmitting = false;
					this._errorService.error(err, 'MFA Challenge', 'modal');
					this.dialogRef.close({ success: false });
				}
			});
	}

	// H5 (amonea): an always-available exit for the LOGIN-mode challenge, which is disableClose and otherwise
	// offers no Cancel/Escape/backdrop — a user who never gets the OTP or lost their authenticator could only
	// reload the tab or enter wrong codes to lockout. Signing out grants no portal access, so it does not weaken
	// the gate. The customer-switch/reauth modes keep their own Cancel and never show this.
	signOut(): void {
		this.dialogRef.close({ success: false, signedOut: true } as MfaChallengeResult);
	}

	verifyCode(): void {
		if (this.isSubmitting) return;   // F-141: (keyup.enter) fires this directly, past the disabled button
		if (!this.code || this.code.length !== 6) {
			this.errorMessage = 'Please enter a 6-digit code.';
			return;
		}

		this.isSubmitting = true;
		this.errorMessage = '';

		if (this.data.mode === 'reauth') {
			this._reAuthService.verifyReAuth(
				this.data.challengeSessionId,
				this.code,
				this.currentMethod
			)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (result) => {
						this.isSubmitting = false;

						if (result.success) {
							this.currentStep = 'success';
							setTimeout(() => {
								this.dialogRef.close({
									success: true,
									reAuthToken: result.reAuthToken
								} as MfaChallengeResult);
							}, 1000);
							return;
						}

						if (result.lockedOut) {
							this.contactAdminPhoneEnabled = result.contactAdminPhoneEnabled;
							this.contactAdminPhone = result.contactAdminPhone || '';
							this.contactAdminEmailEnabled = result.contactAdminEmailEnabled;
							this.contactAdminEmail = result.contactAdminEmail || '';
							this.currentStep = 'contact-admin';
							return;
						}

						this.code = '';
						this.attemptsRemaining = result.attemptsRemaining;
						// F-154: the count is owned by the .attempts-info line (which branches on null); duplicating
						// it here rendered one fact in two alarm colours and printed "null attempts remaining" when
						// the server returned attemptsRemaining: null.
						this.errorMessage = 'Invalid code.';
					},
					error: (err) => {
						this.isSubmitting = false;
						this.code = '';
						this.errorMessage = err?.error?.message || 'Verification failed. Please try again.';
					}
				});
		} else {
			this._challengeService.verifyChallenge(
				this.data.challengeSessionId,
				this.code,
				this.currentMethod,
				this.data.custId
			)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (result) => {
						this.isSubmitting = false;

						if (result.success) {
							this.currentStep = 'success';
							setTimeout(() => {
								this.dialogRef.close({
									success: true,
									mfaSessionToken: result.mfaSessionToken,
									method: this.currentMethod
								} as MfaChallengeResult);
							}, 1000);
							return;
						}

						if (result.lockedOut) {
							this.contactAdminPhoneEnabled = result.contactAdminPhoneEnabled;
							this.contactAdminPhone = result.contactAdminPhone || '';
							this.contactAdminEmailEnabled = result.contactAdminEmailEnabled;
							this.contactAdminEmail = result.contactAdminEmail || '';
							this.currentStep = 'contact-admin';
							return;
						}

						this.code = '';
						this.attemptsRemaining = result.attemptsRemaining;
						// F-154: the count is owned by the .attempts-info line (which branches on null); duplicating
						// it here rendered one fact in two alarm colours and printed "null attempts remaining" when
						// the server returned attemptsRemaining: null.
						this.errorMessage = 'Invalid code.';
					},
					error: (err) => {
						this.isSubmitting = false;
						this.code = '';
						this.errorMessage = err?.error?.message || 'Verification failed. Please try again.';
					}
				});
		}
	}

	switchMethod(): void {
		if (this.isSubmitting) return;   // F-141: guard the in-flight window against a second trigger
		this.isSubmitting = true;
		this.errorMessage = '';

		const switch$ = this.data.mode === 'reauth'
			? this._reAuthService.switchMethod(this.data.challengeSessionId)
			: this._challengeService.switchMethod(this.data.challengeSessionId);

		switch$
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.isSubmitting = false;
					this.hasSwitched = true;
					this.isOnAlternativeMethod = true;
					this.currentMethod = result.alternativeMethod;
					this.code = '';
					this.errorMessage = '';

					if (result.alternativeMethod === 'EmailOtp' && result.otpExpiresAt) {
						this.otpExpiresAt = new Date(result.otpExpiresAt);
						this.startOtpCountdown();
						this.startResendCountdown(result.otpResendDelaySeconds);
					}

					this.currentStep = result.alternativeMethod === 'EmailOtp' ? 'email-otp' : 'totp';
					this.updateComputedProperties();
				},
				error: (err) => {
					this.isSubmitting = false;
					this.errorMessage = err?.error?.message || 'Failed to switch method.';
				}
			});
	}

	cancelSwitch(): void {
		// Tell the SERVER, not just the UI. This used to flip local state only (and re-Start the challenge for
		// the email case), leaving the server still on the alternative factor — so Resend was then rejected
		// with "Resend is only available for email OTP challenges." while the user was looking at the email
		// step with an enabled Resend button (FIX-15).
		//
		// The switch stays SPENT server-side, so "Try another method" correctly remains unavailable; the
		// endpoint also sends a fresh OTP when reverting to email, which is why startChallenge() must NOT be
		// called here any more — doing so inserted a second attempt row for the same session.
		if (this.isSubmitting) return;   // F-141: guard the in-flight window against a second trigger
		this.isSubmitting = true;
		this.errorMessage = '';

		const cancel$ = this.data.mode === 'reauth'
			? this._reAuthService.cancelSwitch(this.data.challengeSessionId)
			: this._challengeService.cancelSwitch(this.data.challengeSessionId);

		cancel$
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.isSubmitting = false;
					this.isOnAlternativeMethod = false;
					this.currentMethod = result.alternativeMethod || this.originalMethod;
					this.code = '';
					this.errorMessage = '';
					this.currentStep = this.currentMethod === 'EmailOtp' ? 'email-otp' : 'totp';

					if (this.currentMethod === 'EmailOtp' && result.otpExpiresAt) {
						this.otpExpiresAt = new Date(result.otpExpiresAt);
						this.startOtpCountdown();
						this.startResendCountdown(result.otpResendDelaySeconds);
					}
					this.updateComputedProperties();
				},
				error: (err) => {
					this.isSubmitting = false;
					this.errorMessage = err?.error?.message || 'Failed to cancel the method switch.';
				}
			});
	}

	resendCode(): void {
		if (this.isSubmitting) return;   // F-141: guard against a double resend burning the cooldown
		this.code = '';
		this.errorMessage = '';
		this.isSubmitting = true;

		const resend$ = this.data.mode === 'reauth'
			? this._reAuthService.resendOtp(this.data.challengeSessionId)
			: this._challengeService.resendOtp(this.data.challengeSessionId);

		resend$
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.isSubmitting = false;
					this.otpExpiresAt = new Date(result.otpExpiresAt);
					this.startOtpCountdown();
					this.startResendCountdown(result.retryAfterSeconds);
					this.updateComputedProperties();
				},
				error: (err) => {
					this.isSubmitting = false;
					this.errorMessage = err?.error?.message || 'Failed to resend code.';
				}
			});
	}

	private startOtpCountdown(): void {
		if (!this.otpExpiresAt) return;

		this.updateOtpCountdown();
		interval(1000)
			.pipe(
				takeUntil(this.destroy$),
				takeWhile(() => this.otpCountdown > 0)
			)
			.subscribe(() => this.updateOtpCountdown());
	}

	private updateOtpCountdown(): void {
		if (!this.otpExpiresAt) return;
		const remaining = Math.max(0, Math.floor((this.otpExpiresAt.getTime() - Date.now()) / 1000));
		this.otpCountdown = remaining;
		if (remaining === 0) {
			this.errorMessage = 'This code has expired. Please request a new code.';
		}
		this.updateComputedProperties();
	}

	private startResendCountdown(seconds: number): void {
		this.resendCountdown = seconds;
		interval(1000)
			.pipe(
				takeUntil(this.destroy$),
				takeWhile(() => this.resendCountdown > 0)
			)
			.subscribe(() => {
				this.resendCountdown--;
				this.updateComputedProperties();
			});
	}

	sanitizeCode(event: Event): void {
		const input = event.target as HTMLInputElement;
		input.value = input.value.replace(/[^0-9]/g, '');
		this.code = input.value;
	}

	private updateComputedProperties(): void {
		this.canSwitchMethod = this.data.availableMethods
			&& this.data.availableMethods.length > 1
			&& !this.hasSwitched
			&& !this.isOnAlternativeMethod;
		this.switchMethodLabel = this.buildSwitchMethodLabel();
		this.isOtpExpired = this.currentMethod === 'EmailOtp' && this.otpCountdown <= 0 && this.otpExpiresAt !== null;
		this.canResend = this.currentMethod === 'EmailOtp' && (this.resendCountdown <= 0 || this.isOtpExpired);
		const minutes = Math.floor(this.otpCountdown / 60);
		const seconds = this.otpCountdown % 60;
		this.formattedCountdown = `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	/**
	 * Names the single factor the user would switch to. Falls back to the generic label when the destination
	 * cannot be identified — with more than two enrolled factors there is no one destination to name, and the
	 * server picks it, so promising a specific factor here would be a guess.
	 */
	/**
	 * Steps where the dialog's work is OVER: each carries its own single exit (Close on contact-admin, an
	 * automatic close on success), so the shared action-row controls must stand down. Expressed once, as a
	 * concept, rather than repeated as `!== 'success' && !== 'contact-admin'` at each site — patching those
	 * conditions case by case is what produced CTA-5 and CTA-7, and a step added to ChallengeStep without
	 * updating every site would reintroduce the same class of bug.
	 */
	get isTerminalStep(): boolean {
		return this.currentStep === 'success' || this.currentStep === 'contact-admin';
	}

	/**
	 * Names the factor the user goes BACK to after switching. It must not read "Cancel": the dialog's own
	 * Cancel sits in the action row at the same time (customer-switch and re-auth modes), and two buttons
	 * labelled identically with different consequences — step back vs abandon the whole operation — is the
	 * ambiguity this label exists to remove. Reuses the same copy as the forward switch, mirrored.
	 */
	get cancelSwitchLabel(): string {
		return SWITCH_METHOD_LABELS[this.originalMethod] || 'Go back';
	}

	private buildSwitchMethodLabel(): string {
		const others = (this.data.availableMethods || []).filter((m) => m !== this.currentMethod);
		if (others.length !== 1) {
			return SWITCH_FALLBACK_LABEL;
		}
		return SWITCH_METHOD_LABELS[others[0]] || SWITCH_FALLBACK_LABEL;
	}
}
